import React, { useState } from 'react';

interface BedrockOptionsProps {
  settings: any;
  onSettingChange: (key: string, value: string) => void;
}

const AWS_REGIONS = [
  { code: 'us-east-1', name: 'US East (N. Virginia)' },
  { code: 'us-east-2', name: 'US East (Ohio)' },
  { code: 'us-west-1', name: 'US West (N. California)' },
  { code: 'us-west-2', name: 'US West (Oregon)' },
  { code: 'af-south-1', name: 'Africa (Cape Town)' },
  { code: 'ap-east-1', name: 'Asia Pacific (Hong Kong)' },
  { code: 'ap-south-1', name: 'Asia Pacific (Mumbai)' },
  { code: 'ap-northeast-3', name: 'Asia Pacific (Osaka)' },
  { code: 'ap-northeast-2', name: 'Asia Pacific (Seoul)' },
  { code: 'ap-southeast-1', name: 'Asia Pacific (Singapore)' },
  { code: 'ap-southeast-2', name: 'Asia Pacific (Sydney)' },
  { code: 'ap-northeast-1', name: 'Asia Pacific (Tokyo)' },
  { code: 'ca-central-1', name: 'Canada (Central)' },
  { code: 'eu-central-1', name: 'Europe (Frankfurt)' },
  { code: 'eu-west-1', name: 'Europe (Ireland)' },
  { code: 'eu-west-2', name: 'Europe (London)' },
  { code: 'eu-south-1', name: 'Europe (Milan)' },
  { code: 'eu-west-3', name: 'Europe (Paris)' },
  { code: 'eu-north-1', name: 'Europe (Stockholm)' },
  { code: 'me-south-1', name: 'Middle East (Bahrain)' },
  { code: 'sa-east-1', name: 'South America (São Paulo)' }
];

export const BedrockOptions: React.FC<BedrockOptionsProps> = ({ settings, onSettingChange }) => {
  const [connectionMethod, setConnectionMethod] = useState(
    settings?.AwsBedrockLLMConnectionMethod || 'iam'
  );

  const handleConnectionMethodChange = (value: string) => {
    setConnectionMethod(value);
    onSettingChange('AwsBedrockLLMConnectionMethod', value);
  };

  return (
    <div className="space-y-6">
      {/* Informação sobre IAM */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Configuração de Autenticação AWS
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Recomendamos usar um usuário IAM adequadamente configurado para inferência.
                Certifique-se de que o usuário tenha as permissões necessárias para acessar o Amazon Bedrock.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Método de Conexão */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Método de Autenticação
        </label>
        <select
          value={connectionMethod}
          onChange={(e) => handleConnectionMethodChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="iam">IAM (Credenciais Explícitas)</option>
          <option value="sessionToken">Token de Sessão (Credenciais Temporárias)</option>
          <option value="iam_role">Função IAM (Credenciais Implícitas)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Selecione o método para autenticar com o AWS Bedrock
        </p>
      </div>

      {/* Credenciais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['iam', 'sessionToken'].includes(connectionMethod) && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID da Chave de Acesso IAM
              </label>
              <input
                type="password"
                value={settings?.AwsBedrockLLMAccessKeyId || ''}
                onChange={(e) => onSettingChange('AwsBedrockLLMAccessKeyId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AKIA..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave de Acesso Secreta IAM
              </label>
              <input
                type="password"
                value={settings?.AwsBedrockLLMAccessKey || ''}
                onChange={(e) => onSettingChange('AwsBedrockLLMAccessKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Chave secreta"
                required
              />
            </div>
          </>
        )}

        {connectionMethod === 'sessionToken' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Token de Sessão
            </label>
            <input
              type="password"
              value={settings?.AwsBedrockLLMSessionToken || ''}
              onChange={(e) => onSettingChange('AwsBedrockLLMSessionToken', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Token de sessão"
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Região AWS
          </label>
          <select
            value={settings?.AwsBedrockLLMRegion || 'us-west-2'}
            onChange={(e) => onSettingChange('AwsBedrockLLMRegion', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            {AWS_REGIONS.map((region) => (
              <option key={region.code} value={region.code}>
                {region.name} ({region.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Configurações do Modelo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ID do Modelo
          </label>
          <input
            type="text"
            value={settings?.AwsBedrockLLMModel || ''}
            onChange={(e) => onSettingChange('AwsBedrockLLMModel', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="meta.llama3.1-v0.1"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            ID do modelo do AWS Bedrock
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Janela de Contexto
          </label>
          <input
            type="number"
            value={settings?.AwsBedrockLLMTokenLimit || ''}
            onChange={(e) => onSettingChange('AwsBedrockLLMTokenLimit', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="8192"
            min="1"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Limite da janela de contexto (ex: 8192)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tokens de Saída Máximos
          </label>
          <input
            type="number"
            value={settings?.AwsBedrockLLMMaxOutputTokens || ''}
            onChange={(e) => onSettingChange('AwsBedrockLLMMaxOutputTokens', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="4096"
            min="1"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Máximo de tokens de saída (ex: 4096)
          </p>
        </div>
      </div>
    </div>
  );
};



